import { useCallback, useMemo } from 'react';
import useQueryParams from './useQueryParams';

// access_code_failed: Recipient used incorrect access code.
// cancel: Recipient canceled the signing operation, possibly by using the Finish Later option.
// decline: Recipient declined to sign.
// exception: A system error occurred during the signing process.
// fax_pending: Recipient has a fax pending.
// id_check_failed: Recipient failed an ID check.
// session_timeout: The session timed out. An account can control this timeout by using the Signer Session Timeout option.
// signing_complete: The recipient completed the signing ceremony.
// ttl_expired: The Time To Live token for the envelope has expired. After being successfully invoked, these tokens expire after 5 minutes or if the envelope is voided.
// viewing_complete: The recipient completed viewing an envelope that is in a read-only/terminal state, such as completed, declined, or voided.

const DOCUSIGN_EVENTS = {
  SIGNING_COMPLETE: 'signing_complete',
  DECLINE: 'decline',
} as const;

const NDA_REDIRECTED_ACTIONS = {
  NOT_REDIRECTED: 'not_redirected',
  WAITING: 'waiting',
  REDIRECTED: 'redirected',
} as const;

export default function useDocusignRedirect() {
  const {
    params: { redirectedFromDocusign, event },
    removeQueryParam,
  } = useQueryParams();

  const removeDocusignQueryParams = useCallback(() => {
    removeQueryParam('redirectedFromDocusign');
    removeQueryParam('event');
  }, [removeQueryParam]);

  const docusignRedirect = useMemo(() => {
    if (!redirectedFromDocusign) return NDA_REDIRECTED_ACTIONS.NOT_REDIRECTED;

    if (
      event === DOCUSIGN_EVENTS.SIGNING_COMPLETE ||
      event === DOCUSIGN_EVENTS.DECLINE
    )
      return NDA_REDIRECTED_ACTIONS.WAITING;

    return NDA_REDIRECTED_ACTIONS.REDIRECTED;
  }, [event, redirectedFromDocusign]);

  const isWaitingDocusingWebhook = useMemo(
    () => docusignRedirect === NDA_REDIRECTED_ACTIONS.WAITING,
    [docusignRedirect],
  );

  return {
    docusignRedirect,
    isWaitingDocusingWebhook,
    removeDocusignQueryParams,
  };
}
